import * as THREE from 'three';
import { Entity, bossHpScale, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';
import { Bullet } from './Bullet';
import { Shell } from './Shell';
import { makeGlowSprite } from './energyGlow';
import { useStore } from '../../state/store';
import type { Building, Village } from '../Scene';

/**
 * The Juggernaut — a colossal steampunk land battleship that rolls in from the world
 * edge. The hull and the multiple turrets on its back are completely armored: the only
 * vulnerable spot is the glowing energy maw on its front, which is defended by a ring
 * of close-range machine guns that fire at the player. Bring down the maw and the
 * whole thing detonates in a mushroom cloud + ground-radiating shockwave ring.
 */
const HULL_HALF_W = 26;
const HULL_HALF_L = 50;
const HULL_HEIGHT = 18;
const SPEED = 5.5;
const TURRET_COUNT = 5;
const MAW_HP = 60;
const TURRET_HP = 8;
const STEAMROLL_RADIUS_FACTOR = 1.0; // village destroyed if hull bbox overlaps it
const MAW_GUN_RANGE = 700;
const MAW_GUN_FIRE_INTERVAL = 0.55;
const TURRET_FIRE_INTERVAL = 4.0;
const SCORE = 10000;

interface Turret {
  id: string;
  group: THREE.Group;
  hp: number;
  destroyed: boolean;
  fireCooldown: number;
  /** Local turret-axis pivot (yaw) */
  yawPivot: THREE.Group;
  cannon: THREE.Object3D;
}

interface MawGun {
  group: THREE.Group;
  cooldown: number;
}

type Stage = 'rolling' | 'detonating' | 'done';

export class Juggernaut extends Entity {
  stage: Stage = 'rolling';
  private dieTimer = 0;
  private mawHp = Math.round(MAW_HP * bossHpScale());
  private mawHpMax = this.mawHp;
  /** Inhale wind-up cycle: the maw periodically charges a heavy shot. While inhaling it takes
   *  DOUBLE damage (the punish window); at the end it spits a slow heavy shell to dodge. */
  private inhaleTimer = 5.0;
  private inhaling = false;
  private turrets: Turret[] = [];
  private mawGuns: MawGun[] = [];
  forward = new THREE.Vector3(0, 0, -1);
  /** Speed exposed for radar and Doppler parity. */
  readonly speed = SPEED;
  private currentTarget: Village | null = null;
  private steamrollSet = new WeakSet<Building>();
  private maw: THREE.Mesh;
  private mawCore: THREE.Mesh;
  private mawPulse = 0;
  private smokestackTimer = 0;
  private ringMesh: THREE.Mesh | null = null;
  private mushroomTimer = 0;

  constructor(spawnPos: THREE.Vector3) {
    super('tank');
    this.isBoss = true;
    this.hp = 999;
    this.radius = 60;
    this.group.position.copy(spawnPos); // spawnPos.y is the terrain surface (set by Game)

    // Build hull + parts
    const built = makeJuggernautMesh();
    this.group.add(built.mesh);
    this.maw = built.maw;
    this.mawCore = built.mawCore;
    this.turrets = built.turrets;
    for (const p of this.turrets) p.hp = Math.round(p.hp * bossHpScale()); // later waves, tougher parts
    this.mawGuns = built.mawGuns;

    // Multi-part hit volumes:
    //  - 'maw' part is the only one that damages the boss
    //  - 'turret0..N' each take damage but only kill that turret
    //  - 'hull' is a giant catch-all that absorbs hits with no effect (so bullets that
    //    miss the maw at extreme angles still terminate visibly)
    this.hitParts = [];
    // Maw box (front of vehicle): around the glowing core
    this.hitParts.push({
      id: 'maw',
      box: new THREE.Box3(new THREE.Vector3(-7, -7, -7), new THREE.Vector3(7, 7, 7)),
      object: this.maw,
    });
    // Turret hit boxes
    for (const t of this.turrets) {
      this.hitParts.push({
        id: t.id,
        box: new THREE.Box3(new THREE.Vector3(-3, -2, -3), new THREE.Vector3(3, 4, 3)),
        object: t.group,
      });
    }
    // No hull catch-all — bullets that miss the maw or a turret simply fly through.
    // The catch-all version was eating every shot before it could reach the maw.
  }

  alive(): boolean { return this.stage === 'rolling'; }

  damagePart(partId: string, amount: number, ctx: EntityCtx): void {
    if (this.stage !== 'rolling') return;
    if (partId === 'maw') {
      // Punish window: the maw is soft while it inhales for its heavy shot.
      this.mawHp -= this.inhaling ? amount * 2 : amount;
      // Visual hit feedback at the maw
      const wp = new THREE.Vector3();
      this.maw.getWorldPosition(wp);
      new Explosion(wp, this.inhaling ? 9 : 6, 0.3, 'fire').addTo(ctx);
      if (this.mawHp <= 0) this.beginDying(ctx);
      return;
    }
    // (No hull part — bullets that miss the maw/turrets just keep going.)
    // Turret hit
    const t = this.turrets.find((tt) => tt.id === partId);
    if (t && !t.destroyed) {
      t.hp -= amount;
      const wp = new THREE.Vector3();
      t.group.getWorldPosition(wp);
      new Explosion(wp, 4, 0.2, 'fire').addTo(ctx);
      if (t.hp <= 0) this.destroyTurret(t, ctx);
    }
  }

  private destroyTurret(t: Turret, ctx: EntityCtx) {
    t.destroyed = true;
    const wp = new THREE.Vector3();
    t.group.getWorldPosition(wp);
    new Explosion(wp, 24, 0.7, 'fire').addTo(ctx);
    new Explosion(wp, 18, 3.5, 'smoke').setDrift(new THREE.Vector3(0, 9, 0)).addTo(ctx);
    ctx.game.audio.playBoom(0.6);
    if (this.hitParts) this.hitParts = this.hitParts.filter((p) => p.id !== t.id);
    // Visual: tilt the turret down and darken
    t.group.rotation.z = (Math.random() < 0.5 ? -1 : 1) * 0.6;
    t.group.position.y -= 0.5;
    t.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        const mat = m.material as THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[];
        if (Array.isArray(mat)) mat.forEach((mm) => mm.color.setHex(0x1c1814));
        else mat.color?.setHex(0x1c1814);
      }
    });
  }

  update(ctx: EntityCtx): void {
    const { dt } = ctx;
    if (this.stage === 'detonating') { this.updateDying(ctx); return; }
    if (this.stage === 'done') { this.dead = true; return; }

    // Pulsate the glowing maw core (sin-wave scale + emissive intensity)
    this.mawPulse += dt * 4.5;
    const pulse = 0.85 + 0.18 * Math.sin(this.mawPulse);
    this.mawCore.scale.set(pulse, pulse, pulse);
    const coreMat = this.mawCore.material as THREE.MeshBasicMaterial;
    coreMat.color.setRGB(0.7 + 0.3 * Math.sin(this.mawPulse * 1.7),
                         0.3 + 0.2 * Math.sin(this.mawPulse * 0.8),
                         1.0);

    // Re-target if village got bombed elsewhere
    if (!this.currentTarget || this.currentTarget.destroyed) this.pickTarget(ctx);
    const target = this.currentTarget;
    if (target) {
      const toTarget = target.position.clone().sub(this.group.position);
      toTarget.y = 0;
      const dist = toTarget.length();
      if (dist > 0.001) toTarget.multiplyScalar(1 / dist);
      this.forward.lerp(toTarget, 0.02).normalize();
      const yaw = Math.atan2(this.forward.x, this.forward.z);
      this.group.rotation.y = yaw;
      this.group.position.addScaledVector(this.forward, SPEED * dt);
    }
    // Track the rolling terrain so the hull stays grounded as it drives.
    this.group.position.y = ctx.game.gameScene.heightAt(this.group.position.x, this.group.position.z);

    // Steamroll: any building under the rolling hull is crushed.
    this.steamrollBuildings(ctx);

    // Smokestacks belch smoke
    this.smokestackTimer += dt;
    if (this.smokestackTimer >= 0.25) {
      this.smokestackTimer = 0;
      for (let i = -1; i <= 1; i += 2) {
        const stackLocal = new THREE.Vector3(i * 12, 22, -8);
        stackLocal.applyQuaternion(this.group.quaternion);
        new Explosion(this.group.position.clone().add(stackLocal), 7, 4, 'smoke')
          .setDrift(new THREE.Vector3(0, 12, 0)).addTo(ctx);
      }
    }

    // Live turrets fire at villages
    for (const t of this.turrets) {
      if (t.destroyed) continue;
      t.fireCooldown -= dt;
      if (t.fireCooldown <= 0 && target) {
        t.fireCooldown = TURRET_FIRE_INTERVAL + Math.random() * 1.5;
        this.fireTurretShell(t, ctx, target);
      }
      // Track turret yaw toward target
      if (target) {
        const localTarget = target.position.clone();
        this.group.updateWorldMatrix(true, false);
        localTarget.applyMatrix4(new THREE.Matrix4().copy(this.group.matrixWorld).invert());
        t.yawPivot.rotation.y = Math.atan2(localTarget.x, -localTarget.z);
      }
    }

    // Maw guns fire at the nearest friendly in range — attacking armor draws the ring of MGs
    // (faster when enraged; silent while inhaling — the wind-up IS the tell).
    const enraged = this.mawHp <= this.mawHpMax / 2;
    const mawTarget = ctx.game.nearestFriendly(this.group.position, MAW_GUN_RANGE);
    if (mawTarget && !this.inhaling) {
      for (const g of this.mawGuns) {
        g.cooldown -= dt;
        if (g.cooldown <= 0) {
          g.cooldown = (enraged ? MAW_GUN_FIRE_INTERVAL * 0.6 : MAW_GUN_FIRE_INTERVAL) + Math.random() * 0.4;
          this.fireMawGun(g, ctx, mawTarget.position);
        }
      }
    }

    // INHALE cycle — the maw charges a heavy shot: it glows and swells (taking 2x damage, see
    // damagePart), then spits a slow heavy shell at the player that must be dodged.
    this.inhaleTimer -= dt;
    if (this.inhaleTimer <= 0) {
      if (!this.inhaling) {
        this.inhaling = true;
        this.inhaleTimer = 2.5; // wind-up duration = the punish window
        ctx.game.audio.playBoom(0.4);
      } else {
        this.inhaling = false;
        this.inhaleTimer = (enraged ? 5 : 7) + Math.random() * 1.5;
        // The heavy shot: slow, fat, lethal — and very dodgeable if you're paying attention.
        // Aimed at whatever friendly is pressing the attack (player if nothing's closer).
        const heavyAt = (ctx.game.nearestFriendly(this.group.position, 1500) ?? ctx.game.player).position;
        const muzzle = new THREE.Vector3();
        this.maw.getWorldPosition(muzzle);
        const dir = heavyAt.clone().sub(muzzle).normalize();
        const shell = new Shell(muzzle, dir.multiplyScalar(130), this.id, 'enemy');
        shell.damageAmount = 6;
        shell.impactScale = 9;
        shell.ttl = 8;
        ctx.game.add(shell);
        new Explosion(muzzle.clone(), 26, 0.5, 'fire').addTo(ctx);
        ctx.game.audio.playBoom(1.0);
      }
    }
    // The maw core swells and burns white-hot through the wind-up.
    if (this.inhaling) {
      const t = 1 - this.inhaleTimer / 2.5;
      this.mawCore.scale.setScalar(pulse * (1 + 0.8 * t));
      coreMat.color.setRGB(1.0, 0.55 + 0.45 * t, 0.9 + 0.1 * t);
    }
  }

  private steamrollBuildings(ctx: EntityCtx) {
    // Test each village's buildings against the rolling hull's world AABB.
    this.group.updateWorldMatrix(true, false);
    const inv = new THREE.Matrix4().copy(this.group.matrixWorld).invert();
    for (const v of ctx.game.gameScene.villages) {
      if (v.destroyed) continue;
      // Quick reject: if village center is far from juggernaut, skip.
      const dx = v.position.x - this.group.position.x;
      const dz = v.position.z - this.group.position.z;
      if (Math.hypot(dx, dz) > 200) continue;
      for (const bld of v.buildings) {
        if (bld.destroyed || this.steamrollSet.has(bld)) continue;
        const local = bld.position.clone().applyMatrix4(inv);
        if (Math.abs(local.x) <= HULL_HALF_W * STEAMROLL_RADIUS_FACTOR &&
            Math.abs(local.z) <= HULL_HALF_L * STEAMROLL_RADIUS_FACTOR) {
          this.steamrollSet.add(bld);
          // Crushed — small puff, no big explosion (it's getting flattened, not blown up)
          new Explosion(bld.position.clone(), 8, 1.4, 'smoke')
            .setDrift(new THREE.Vector3(0, 5, 0)).addTo(ctx);
          bld.destroy(ctx.game.gameScene.root);
        }
      }
    }
  }

  private fireTurretShell(t: Turret, ctx: EntityCtx, target: Village) {
    const livePool: Building[] = target.buildings.filter((b) => !b.destroyed);
    if (livePool.length === 0) return;
    const victim = livePool[Math.floor(Math.random() * livePool.length)];
    const muzzleWorld = new THREE.Vector3();
    t.cannon.getWorldPosition(muzzleWorld);
    new Explosion(muzzleWorld, 7, 0.16, 'fire').addTo(ctx);
    new Explosion(muzzleWorld, 5, 0.5, 'smoke').setDrift(new THREE.Vector3(0, 4, 0)).addTo(ctx);
    ctx.game.audio.playBoom(0.5);
    new Explosion(victim.position.clone(), 22, 0.8, 'fire').addTo(ctx);
    new Explosion(victim.position.clone(), 16, 3.0, 'smoke')
      .setDrift(new THREE.Vector3(0, 8, 0)).addTo(ctx);
    victim.destroy(ctx.game.gameScene.root);
  }

  private fireMawGun(g: MawGun, ctx: EntityCtx, target: THREE.Vector3) {
    const muzzle = new THREE.Vector3();
    g.group.getWorldPosition(muzzle);
    const dir = target.clone().sub(muzzle).normalize();
    // Add a tiny spread so the maw guns aren't laser-accurate.
    dir.x += (Math.random() - 0.5) * 0.04;
    dir.y += (Math.random() - 0.5) * 0.04;
    dir.z += (Math.random() - 0.5) * 0.04;
    dir.normalize();
    const vel = dir.multiplyScalar(260);
    ctx.game.add(new Bullet(muzzle, vel, this.id, 'enemy'));
  }

  private pickTarget(ctx: EntityCtx) {
    let best: Village | null = null;
    let bestD = Infinity;
    for (const v of ctx.game.gameScene.villages) {
      if (v.destroyed) continue;
      const dx = v.position.x - this.group.position.x;
      const dz = v.position.z - this.group.position.z;
      const d = Math.hypot(dx, dz);
      if (d < bestD) { bestD = d; best = v; }
    }
    this.currentTarget = best;
  }

  private beginDying(ctx: EntityCtx) {
    this.stage = 'detonating';
    this.dieTimer = 0;
    useStore.getState().registerKill(SCORE);
    // Initial maw shatter
    const mawWp = new THREE.Vector3();
    this.maw.getWorldPosition(mawWp);
    new Explosion(mawWp, 50, 1.0, 'fire').addTo(ctx);
    ctx.game.audio.playBoom(1.5);
    ctx.game.audio.playHydrogenRoar();
  }

  private updateDying(ctx: EntityCtx) {
    const dt = ctx.dt;
    this.dieTimer += dt;
    const t = this.dieTimer;

    // Stage 1 (0..0.4s): chain of explosions across the hull
    if (t < 0.4) {
      if (Math.random() < 0.7) {
        const off = new THREE.Vector3(
          (Math.random() - 0.5) * HULL_HALF_W * 1.6,
          5 + Math.random() * 12,
          (Math.random() - 0.5) * HULL_HALF_L * 1.6,
        );
        const p = this.group.position.clone().add(off);
        new Explosion(p, 24, 0.6, 'fire').addTo(ctx);
      }
    }
    // Stage 2 (0.3s): central blast + ring spawn
    if (t >= 0.3 && !this.ringMesh) {
      const center = this.group.position.clone();
      center.y = -460;
      // Big central fire
      new Explosion(center, 280, 1.2, 'fire').addTo(ctx);
      new Explosion(center, 180, 0.8, 'fire').addTo(ctx);
      // Spawn the horizontal shockwave ring (a torus we expand over time)
      const ringGeom = new THREE.TorusGeometry(1, 5, 12, 64);
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0xffd9a0, transparent: true, opacity: 0.85,
        side: THREE.DoubleSide, depthWrite: false,
      });
      this.ringMesh = new THREE.Mesh(ringGeom, ringMat);
      this.ringMesh.rotation.x = Math.PI / 2;
      this.ringMesh.position.copy(center);
      this.ringMesh.position.y += 5;
      ctx.game.scene.add(this.ringMesh);
      ctx.game.audio.playBoom(2.0);
    }
    // Animate ring expansion + fade
    if (this.ringMesh) {
      const tRing = Math.max(0, t - 0.3);
      const scale = 1 + tRing * 320; // grows fast
      this.ringMesh.scale.set(scale, scale, scale);
      const m = this.ringMesh.material as THREE.MeshBasicMaterial;
      m.opacity = Math.max(0, 0.85 * (1 - tRing / 1.6));
      if (tRing > 1.6) {
        ctx.game.scene.remove(this.ringMesh);
        m.dispose();
        (this.ringMesh.geometry as THREE.BufferGeometry).dispose();
        this.ringMesh = null;
      }
    }
    // Stage 3 (0.5s+): mushroom column rises
    if (t >= 0.5) {
      this.mushroomTimer += dt;
      // Stem: dense smoke rising from ground at the center
      if (this.mushroomTimer >= 0.05) {
        this.mushroomTimer = 0;
        const center = this.group.position.clone();
        center.y = -460;
        // Stem puffs at random heights along a vertical column
        const stemY = Math.min(140, t * 80);
        for (let i = 0; i < 3; i++) {
          const off = new THREE.Vector3(
            (Math.random() - 0.5) * 18,
            stemY * (0.2 + Math.random() * 0.8),
            (Math.random() - 0.5) * 18,
          );
          new Explosion(center.clone().add(off), 36 + Math.random() * 20, 5, 'smoke')
            .setDrift(new THREE.Vector3((Math.random() - 0.5) * 4, 22, (Math.random() - 0.5) * 4))
            .addTo(ctx);
        }
        // Once we're past 1.5s, start spawning a wide cap at high altitude (the mushroom top)
        if (t > 1.5) {
          for (let i = 0; i < 4; i++) {
            const a = Math.random() * Math.PI * 2;
            const r = 60 + Math.random() * 80;
            const off = new THREE.Vector3(
              Math.cos(a) * r,
              130 + Math.random() * 60,
              Math.sin(a) * r,
            );
            new Explosion(center.clone().add(off), 70 + Math.random() * 30, 7, 'smoke')
              .setDrift(new THREE.Vector3((Math.random() - 0.5) * 3, 6, (Math.random() - 0.5) * 3))
              .addTo(ctx);
          }
        }
      }
    }
    // Stage 4 (after 5s): wind down
    if (t > 5.0) this.stage = 'done';
  }
}

/* --------------------------------------------------------------------- */
/* Mesh factory                                                           */
/* --------------------------------------------------------------------- */

interface BuildResult {
  mesh: THREE.Group;
  maw: THREE.Mesh;
  mawCore: THREE.Mesh;
  turrets: Turret[];
  mawGuns: MawGun[];
}

function makeJuggernautMesh(): BuildResult {
  const g = new THREE.Group();
  const turrets: Turret[] = [];
  const mawGuns: MawGun[] = [];

  const armor = new THREE.MeshStandardMaterial({ color: 0x4a3a2c, roughness: 0.85, flatShading: true });
  const armorDark = new THREE.MeshStandardMaterial({ color: 0x2c2218, roughness: 0.9, flatShading: true });
  const tread = new THREE.MeshStandardMaterial({ color: 0x141414, roughness: 0.95 });
  const brass = new THREE.MeshStandardMaterial({ color: 0xb88a30, roughness: 0.5, metalness: 0.8 });
  const iron = new THREE.MeshStandardMaterial({ color: 0x252528, roughness: 0.45, metalness: 0.7 });
  const rivetMat = new THREE.MeshStandardMaterial({ color: 0x6a5a44, roughness: 0.55, metalness: 0.7 });

  // Main hull — massive slab
  const hull = new THREE.Mesh(new THREE.BoxGeometry(HULL_HALF_W * 2, HULL_HEIGHT, HULL_HALF_L * 2, 8, 6, 16), armor);
  hull.position.set(0, HULL_HEIGHT / 2 + 5, 0);
  g.add(hull);
  // Stepped upper deck
  const deck = new THREE.Mesh(new THREE.BoxGeometry(HULL_HALF_W * 1.7, 4, HULL_HALF_L * 1.8, 8, 2, 12), armorDark);
  deck.position.set(0, HULL_HEIGHT + 7, 0);
  g.add(deck);
  // Front sloped armor plate
  const glacis = new THREE.Mesh(new THREE.BoxGeometry(HULL_HALF_W * 1.95, HULL_HEIGHT * 0.85, 2, 8, 4, 1), armorDark);
  glacis.rotation.x = -0.3;
  glacis.position.set(0, HULL_HEIGHT * 0.5 + 4, -HULL_HALF_L - 0.5);
  g.add(glacis);

  // Decorative rivets on the sides (just rows of small cylinders)
  for (const sx of [-HULL_HALF_W - 0.05, HULL_HALF_W + 0.05]) {
    for (let z = -HULL_HALF_L + 5; z <= HULL_HALF_L - 5; z += 7) {
      for (let y = 6; y <= HULL_HEIGHT; y += 5) {
        const r = new THREE.Mesh(new THREE.SphereGeometry(0.4, 8, 6), rivetMat);
        r.position.set(sx, y, z);
        g.add(r);
      }
    }
  }

  // Tracks — two pairs each side (front + rear), forming the tank-like silhouette
  for (const sx of [-HULL_HALF_W - 1, HULL_HALF_W + 1]) {
    const trackBody = new THREE.Mesh(new THREE.BoxGeometry(3.5, 9, HULL_HALF_L * 2 + 8, 2, 6, 16), tread);
    trackBody.position.set(sx, 3.5, 0);
    g.add(trackBody);
    // Big drive sprockets at front + rear
    for (const sz of [-HULL_HALF_L - 4, HULL_HALF_L + 4]) {
      const sprocket = new THREE.Mesh(new THREE.CylinderGeometry(4.2, 4.2, 4, 32), tread);
      sprocket.rotation.z = Math.PI / 2;
      sprocket.position.set(sx, 3.5, sz);
      g.add(sprocket);
      const hubCap = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 4.1, 16), brass);
      hubCap.rotation.z = Math.PI / 2;
      hubCap.position.set(sx, 3.5, sz);
      g.add(hubCap);
    }
    // Road wheels
    for (let i = -5; i <= 5; i++) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.2, 3.6, 24), tread);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(sx, 2.4, i * 9);
      g.add(wheel);
    }
  }

  // Smokestacks — two tall brass-banded stacks at the rear of the deck
  for (const sx of [-12, 12]) {
    const stack = new THREE.Mesh(new THREE.CylinderGeometry(2.0, 2.4, 14, 24, 4), iron);
    stack.position.set(sx, HULL_HEIGHT + 14, -8);
    g.add(stack);
    // Brass band
    for (let i = 0; i < 3; i++) {
      const band = new THREE.Mesh(new THREE.CylinderGeometry(2.15, 2.15, 0.6, 24), brass);
      band.position.set(sx, HULL_HEIGHT + 8 + i * 4, -8);
      g.add(band);
    }
  }

  // Pipework along the deck
  for (const sx of [-8, 8]) {
    const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, HULL_HALF_L * 1.5, 14), brass);
    pipe.rotation.x = Math.PI / 2;
    pipe.position.set(sx, HULL_HEIGHT + 11, 4);
    g.add(pipe);
  }

  // Turrets — five distributed on the upper deck.
  const turretLocations: [number, number][] = [
    [0, -22],   // forward-center
    [-12, -8],  // forward-left
    [12, -8],   // forward-right
    [-12, 14],  // rear-left
    [12, 14],   // rear-right
  ];
  for (let i = 0; i < TURRET_COUNT; i++) {
    const [tx, tz] = turretLocations[i];
    const t = makeTurret(armor, armorDark, brass, iron);
    const yawPivot = new THREE.Group();
    yawPivot.add(t.group);
    yawPivot.position.set(tx, HULL_HEIGHT + 11, tz);
    g.add(yawPivot);
    turrets.push({
      id: `turret${i}`,
      group: t.group,
      cannon: t.cannon,
      yawPivot,
      hp: TURRET_HP,
      destroyed: false,
      fireCooldown: 1.5 + i * 0.4,
    });
  }

  // Maw on the front: a recessed glowing core in a metal ring, with MGs around it.
  const mawHousing = new THREE.Mesh(
    new THREE.CylinderGeometry(8, 8, 4, 32),
    armorDark,
  );
  mawHousing.rotation.x = Math.PI / 2;
  mawHousing.position.set(0, HULL_HEIGHT * 0.55 + 5, -HULL_HALF_L - 0.8);
  g.add(mawHousing);
  // Decorative brass collar
  const mawCollar = new THREE.Mesh(
    new THREE.TorusGeometry(8.3, 0.7, 12, 40),
    brass,
  );
  mawCollar.position.set(0, HULL_HEIGHT * 0.55 + 5, -HULL_HALF_L - 2.4);
  g.add(mawCollar);
  // The "maw" hit-box mesh — invisible-ish placeholder + glowing core
  const mawMesh = new THREE.Mesh(
    new THREE.SphereGeometry(6.5, 32, 24),
    new THREE.MeshStandardMaterial({ color: 0x180b08, roughness: 1, metalness: 0 }),
  );
  mawMesh.position.set(0, HULL_HEIGHT * 0.55 + 5, -HULL_HALF_L - 1.8);
  g.add(mawMesh);
  // Glowing energy core inside the maw — uses MeshBasicMaterial so it ignores light.
  const mawCore = new THREE.Mesh(
    new THREE.SphereGeometry(4.0, 32, 24),
    new THREE.MeshBasicMaterial({ color: 0xa050ff }),
  );
  mawCore.position.copy(mawMesh.position);
  mawCore.position.z -= 0.4;
  mawCore.add(makeGlowSprite(0xb070ff, 22));
  g.add(mawCore);
  // Lightning-arc decorative wires from collar to core (pure visual)
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const wire = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.07, 4.0, 6),
      brass,
    );
    wire.position.set(Math.cos(a) * 6, HULL_HEIGHT * 0.55 + 5 + Math.sin(a) * 6, -HULL_HALF_L - 1.8);
    wire.rotation.z = a + Math.PI / 2;
    wire.rotation.x = -0.2;
    g.add(wire);
  }
  // Maw machine guns: 6 short cannons arranged around the maw at the same Z.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const gunGroup = new THREE.Group();
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.18, 2.8, 18),
      iron,
    );
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0, -1.2);
    gunGroup.add(barrel);
    // Mount block
    const mount = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.5, 2, 2, 2), brass);
    mount.position.set(0, 0, 0.3);
    gunGroup.add(mount);
    gunGroup.position.set(
      Math.cos(a) * 9.2,
      HULL_HEIGHT * 0.55 + 5 + Math.sin(a) * 9.2,
      -HULL_HALF_L - 1.0,
    );
    // Slight inward angle
    gunGroup.rotation.set(0, 0, 0);
    gunGroup.lookAt(0, HULL_HEIGHT * 0.55 + 5, -HULL_HALF_L - 30);
    g.add(gunGroup);
    mawGuns.push({ group: gunGroup, cooldown: 1 + i * 0.1 });
  }

  return { mesh: g, maw: mawMesh, mawCore, turrets, mawGuns };
}

function makeTurret(armor: THREE.Material, armorDark: THREE.Material, brass: THREE.Material, iron: THREE.Material): { group: THREE.Group; cannon: THREE.Mesh } {
  const t = new THREE.Group();
  // Squat cylindrical turret
  const body = new THREE.Mesh(new THREE.CylinderGeometry(2.6, 2.9, 2.6, 24, 2), armor);
  body.position.y = 1.3;
  t.add(body);
  const top = new THREE.Mesh(new THREE.CylinderGeometry(2.4, 2.4, 0.8, 24), armorDark);
  top.position.y = 2.7;
  t.add(top);
  // Cannon
  const cannon = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.26, 5.5, 18), iron);
  cannon.rotation.x = Math.PI / 2;
  cannon.position.set(0, 1.4, -3.5);
  t.add(cannon);
  // Mantlet
  const mantlet = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.8, 16), brass);
  mantlet.rotation.x = Math.PI / 2;
  mantlet.position.set(0, 1.4, -1.0);
  t.add(mantlet);
  return { group: t, cannon };
}
